import { useEffect, useRef, useState } from "react";
import { Upload, Loader2, ImageIcon, AlertTriangle } from "lucide-react";
import { toast } from "react-toastify";
import { uploadFoto } from "../../services/perfilApi";

const MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED_MIMES = ["image/jpeg", "image/png"];

export default function ProfilePictureUploader({ contaId, currentUrl, onUpdated }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  function handleSelect(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setError(null);
    if (!ALLOWED_MIMES.includes(f.type)) {
      setError("Use JPEG ou PNG");
      return;
    }
    if (f.size > MAX_BYTES) {
      setError("Foto excede 5MB");
      return;
    }
    setFile(f);
  }

  async function handleUpload() {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const updated = await uploadFoto(contaId, file);
      toast.success("Foto de perfil atualizada");
      onUpdated?.(updated);
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      setError(msg);
    } finally {
      setUploading(false);
    }
  }

  const showImage = previewUrl || currentUrl;

  return (
    <div className="bg-surface-1 border border-white/[0.06] rounded-lg p-5">
      <h4 className="text-sm font-semibold text-white mb-4">Foto de perfil</h4>

      <div className="flex items-start gap-5 flex-wrap">
        <div className="w-32 h-32 rounded-full bg-base border border-white/[0.06] overflow-hidden flex items-center justify-center flex-shrink-0">
          {showImage ? (
            <img src={showImage} alt="Foto de perfil" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon className="w-10 h-10 text-ink-5" />
          )}
        </div>

        <div className="flex-1 space-y-2 min-w-[200px]">
          <p className="text-xs text-ink-3">
            JPEG ou PNG, mínimo 192×192, até 5MB. A Meta exige imagem quadrada.
          </p>
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png"
            onChange={handleSelect}
            className="hidden"
          />
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="bg-surface-2 hover:bg-surface-3 text-white px-3 py-1.5 rounded text-sm font-medium inline-flex items-center gap-2"
            >
              <Upload className="w-4 h-4" /> Escolher imagem
            </button>
            {file && (
              <>
                <span className="text-xs text-ink-3 truncate max-w-[180px]">{file.name}</span>
                <button
                  type="button"
                  onClick={handleUpload}
                  disabled={uploading}
                  className="bg-brand-600 hover:bg-brand-700 disabled:bg-surface-3 text-white px-3 py-1.5 rounded text-sm font-medium inline-flex items-center gap-2"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Enviar
                </button>
              </>
            )}
          </div>
          {error && (
            <div className="bg-red-950 border border-red-900 text-red-300 text-xs rounded p-2 inline-flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
